import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import ebenchLogo from "../../../src/images/ebench_logo.png";
import { useDispatch } from "react-redux";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Button from "@mui/material/Button";
import { useResendOtp } from "../../Hooks/useResendOtp";
import { verifyVendorOtp } from "../../Services/authService";
import setAuthorizationToken from "../../utils/setAutorization";

const VotpVerify = () => {
  const dispatch = useDispatch();
  const { state: LoginData } = useLocation();
  const navigate = useNavigate();
  const { mutate: resendOtp } = useResendOtp();

  const [isLoading, setIsLoading] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(60);
  const [isResendVisible, setIsResendVisible] = useState(false);
  const [otpValues, setOtpValues] = useState(["", "", "", "", "", ""]);

  const inputRefs = useRef([]);

  const email = LoginData?.email;
  const userType = LoginData?.userType;
  const otpCode = otpValues.join("");

  /** ===== Handle OTP Input ===== */
  const handleInputChange = (index, value) => {
    if (!/^\d?$/.test(value)) return; // Only digits allowed
    const updatedOtp = [...otpValues];
    updatedOtp[index] = value;
    setOtpValues(updatedOtp);

    // Move to next box automatically
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otpValues[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  /** ===== Verify OTP Submit ===== */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!otpCode) {
      toast.error("Please enter your OTP!");
      return;
    }

    const payload = { code: otpCode, email };
    try {
      await verifyVendorOtp(payload);
      toast.success("Verification successful!");

      if (LoginData?.logIn) {
        localStorage.setItem("adminid", LoginData?.adminId);
        setAuthorizationToken(LoginData?.token);

        setTimeout(() => {
          switch (LoginData?.userType) {
            case "VENDOR":
              navigate("/vendor_dashboard");
              break;
            case "ADMIN":
              navigate("/admin");
              break;
            default:
              navigate("/candidate_dashboard");
          }
        }, 1000);
      } else {
        navigate("/login");
      }
    } catch (err) {
      toast.error("OTP verification failed. Please try again.");
    }
  };

  /** ===== Handle Resend OTP ===== */
  const handleResendOtp = () => {
    setIsLoading(true);
    resendOtp({ email, userType }, {
      onSuccess: () => {
        setIsLoading(false);
        setSecondsLeft(60);
      },
      onError: () => {
        setIsLoading(false);
      },
    });
  };

  /** ===== Timer Logic ===== */
  useEffect(() => {
    if (secondsLeft <= 0) {
      setIsResendVisible(true);
      return;
    }

    const timer = setInterval(() => {
      setSecondsLeft((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [secondsLeft]);

  return (
    <>
      <ToastContainer />
      {/* ===== HEADER ===== */}
      <header
        className="d-flex justify-content-between align-items-center px-5 py-3 shadow-sm"
        style={{ backgroundColor: "#fff", position: "fixed", top: 0, width: "100%", zIndex: 10 }}
      >
        <div className="landing_logo">
          <Link to="/">
            <img
              src={ebenchLogo}
              alt="Ebench Logo"
              className="responsive-logo-of-without-login"
              style={{ height: 40 }}
            />
          </Link>
        </div>
        <div className="landing_menu_items d-flex align-items-center gap-3">
          <Link to="/login">
            <button
              className="btn btn-outline-primary-custom"
              style={{
                padding: "6px 20px",
                fontWeight: 600,
                borderRadius: "8px",
                border: "1px solid #286a94",
                color: "#286a94",
              }}
            >
              Login
            </button>
          </Link>
        </div>
      </header>

      {/* ===== OTP FORM ===== */}
      <div
        className="otp-page"
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#f8fbff",
          paddingTop: "80px",
        }}
      >
        <div
          className="otp-card"
          style={{
            background: "#fff",
            borderRadius: "16px",
            boxShadow: "0 4px 14px rgba(0,0,0,0.1)",
            padding: "2.5rem 2rem",
            maxWidth: "630px",
            width: "90%",
            textAlign: "center",
          }}
        >
          <h4 style={{ color: "#1e4e70", fontWeight: 700, marginBottom: "1.5rem" }}>
            Enter Verification OTP
          </h4>
          <p style={{ color: "#555", marginBottom: "1rem" }}>Type your 6-digit security code</p>

          <form onSubmit={handleSubmit}>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "0.8rem",
                marginBottom: "1.5rem",
              }}
            >
              {otpValues.map((val, idx) => (
                <input
                  key={idx}
                  type="text"
                  maxLength="1"
                  value={val}
                  onChange={(e) => handleInputChange(idx, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(e, idx)}
                  ref={(el) => (inputRefs.current[idx] = el)}
                  className="otp-box"
                  style={{
                    width: "50px",
                    height: "55px",
                    border: "1px solid #cbd5e1",
                    borderRadius: "8px",
                    fontSize: "22px",
                    textAlign: "center",
                    color: "#1e4e70",
                    fontWeight: 600,
                    background: "#f9fafb",
                  }}
                />
              ))}
            </div>

            <Button
              type="submit"
              variant="contained"
              sx={{
                backgroundImage: "linear-gradient(135deg, #286a94, #1e4e70)",
                color: "#fff",
                fontWeight: 600,
                borderRadius: "8px",
                px: 4,
                py: 1.2,
                textTransform: "none",
                boxShadow: "0 4px 12px rgba(40,106,148,0.25)",
                "&:hover": {
                  backgroundImage: "linear-gradient(135deg, #307aa6, #245b80)",
                  boxShadow: "0 6px 18px rgba(40,106,148,0.35)",
                },
              }}
            >
              Submit
            </Button>

            <div className="mt-4 resend-section text-center">
              {isResendVisible ? (
                <div className="resend-wrapper">
                  <p className="resend-text mb-2">Didn’t get the code?</p>
                  <Button
                    onClick={handleResendOtp}
                    disabled={isLoading}
                    disableElevation
                    disableRipple
                    variant="contained"
                    sx={{
                      backgroundImage: "linear-gradient(135deg, #286a94, #1e4e70)",
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: "14px",
                      textTransform: "none",
                      borderRadius: "8px",
                      px: 3,
                      py: 1,
                      boxShadow: "0 4px 12px rgba(40,106,148,0.25)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        backgroundImage: "linear-gradient(135deg, #307aa6, #245b80)",
                        boxShadow: "0 6px 18px rgba(40,106,148,0.35)",
                        transform: "translateY(-2px)",
                      },
                      "&:disabled": {
                        backgroundImage:
                          "linear-gradient(135deg, rgba(40,106,148,0.4), rgba(30,78,112,0.4))",
                        boxShadow: "none",
                        color: "#f0f0f0",
                      },
                    }}
                  >
                    {isLoading ? "Resending..." : "Resend OTP"}
                  </Button>
                </div>
              ) : (
                <p className="countdown-text mt-2">
                  Resend OTP in{" "}
                  <strong style={{ color: "#286a94" }}>{secondsLeft}s</strong>
                </p>
              )}
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default VotpVerify;
